import type { TeammateAgentConfigSettings } from "@repo/app-types/entities/teammateAgent";
import { Input } from "@repo/ui/Inputs/input";
import { SimpleRadioGroup } from "@repo/ui/Radio/simpleRadioGroup";
import { SimpleRadioOption } from "@repo/ui/Radio/simpleRadioOption";
import type { FC } from "react";

type InboundSettings = NonNullable<TeammateAgentConfigSettings["inbound"]>;
type InboundMode = InboundSettings["mode"];

export interface InboundCallsSectionProps {
  inbound: InboundSettings;
  /** Set while the forwarding number is missing. */
  forwardToError: string | null;
  isDisabled?: boolean;
  onChange: (inbound: InboundSettings) => void;
}

/**
 * How calls to the agent's number are answered: by the agent, or forwarded on
 * to a number the customer already staffs.
 */
export const InboundCallsSection: FC<InboundCallsSectionProps> = (props) => {
  const { inbound, forwardToError, isDisabled, onChange } = props;

  return (
    <section
      aria-labelledby="inbound-calls-heading"
      className="border-t border-border-light pt-8"
    >
      <h2
        id="inbound-calls-heading"
        className="text-base font-semibold text-foreground"
      >
        Inbound calls
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Choose how incoming calls to this number are handled.
      </p>

      <div className="mt-3">
        <SimpleRadioGroup
          value={inbound.mode}
          onChange={(mode) =>
            // Drop the number when switching back to the agent: carrying it
            // would keep reading as an unpublished change and would publish a
            // forwarding number the user backed out of.
            onChange(
              mode === "forward"
                ? { ...inbound, mode }
                : { mode: mode as InboundMode },
            )
          }
        >
          <SimpleRadioOption value="agent" isDisabled={isDisabled}>
            Connect to the agent
          </SimpleRadioOption>
          <div>
            <SimpleRadioOption value="forward" isDisabled={isDisabled}>
              Forward to another number
            </SimpleRadioOption>
            {inbound.mode === "forward" ? (
              <div className="mt-3 ml-6 max-w-xs">
                <Input
                  id="inbound-forwarding-number"
                  label="Forwarding number"
                  value={inbound.forward_to ?? ""}
                  onChange={(event) =>
                    onChange({ ...inbound, forward_to: event.target.value })
                  }
                  placeholder="+1 555 123 4567"
                  autoComplete="tel"
                  errorDescription={forwardToError ?? undefined}
                  isDisabled={isDisabled}
                />
              </div>
            ) : null}
          </div>
        </SimpleRadioGroup>
      </div>
    </section>
  );
};
